/*
 * Mega-menu panels are rendered out of the document — walking the link tree costs ~86ms of
 * database time on every page — and fetched here instead, as soon as this (deferred) script
 * runs on a desktop viewport. Two constraints set that timing:
 *   - the fetch must not wait for the window `load` event: `load` blocks on every image on
 *     the page, which leaves a promptly-opened menu without its link lists;
 *   - below 990px the inline nav is display:none (the header uses the drawer instead), so
 *     fetching there would spend the panel response on a menu that cannot open. The fetch
 *     waits until the viewport first crosses the breakpoint.
 * Nothing is fetched on hover: by the time a pointer can reach the nav the request is
 * already in flight or landed.
 *
 * Degraded states, in order of severity:
 *   - fetch still in flight: opening a menu shows the panel without its link list (the
 *     merchandising tiles, where configured, are still in the document). The list appears
 *     when the fetch lands, even if the menu is open at the time.
 *   - fetch failed: `data-mega-menu-state="error"` hides the empty panels, and top-level
 *     items navigate to the destination the server put in `data-mega-menu-fallback-url`.
 *   - script never runs (blocked, unsupported): panels stay empty. `<noscript>` covers only
 *     scripting being off, so this is the one case the header cannot fully recover from.
 */
(function () {
  const nav = document.querySelector('.header__inline-menu[data-mega-menu-src]');
  if (!nav) return;

  const slots = Array.from(nav.querySelectorAll('.mega-menu__list-slot[data-mega-menu-slot]'));
  if (slots.length === 0) return;

  const source = nav.dataset.megaMenuSrc;
  const sectionKey = nav.dataset.megaMenuKey;

  const RETRY_DELAY = 1500;

  /* The panels are rendered against the fetch URL, not the page the visitor is on, so the
     server marks nothing as current. Restore it here from the address bar. */
  function markCurrentLinks(list) {
    const here = normalisePath(window.location.pathname);
    list.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      // Headings in this menu are "#" links, which a market prefix turns into "/de-de#" —
      // that resolves to the market home page, so on it every heading would look current.
      if (!href || href.includes('#')) return;
      if (normalisePath(new URL(link.href).pathname) !== here) return;
      link.classList.add('mega-menu__link--active');
      link.setAttribute('aria-current', 'page');
    });
  }

  function normalisePath(path) {
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }

  function degrade() {
    nav.dataset.megaMenuState = 'error';
    nav.querySelectorAll('.mega-menu__list-slot').forEach((slot) => slot.removeAttribute('aria-busy'));
    nav.querySelectorAll('details[data-mega-menu-fallback-url]').forEach((details) => {
      details.open = false;
      // The click now navigates, so the control must stop announcing as a disclosure.
      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('role', 'link');
        summary.removeAttribute('aria-expanded');
        summary.removeAttribute('aria-haspopup');
      }
      details.addEventListener('click', (event) => {
        event.preventDefault();
        window.location.assign(details.dataset.megaMenuFallbackUrl);
      });
    });
  }

  function inject(html) {
    const panels = new DOMParser().parseFromString(html, 'text/html');
    const lists = slots.map((slot) => {
      const panel = panels.getElementById(`MegaMenu-Content-${slot.dataset.megaMenuSlot}`);
      return panel && panel.querySelector('.mega-menu__list');
    });

    // All or nothing: a half-filled nav is worse than the fallback, which at least navigates.
    if (lists.some((list) => !list)) {
      throw new Error('mega-menu-panels: rendered section is missing a panel list');
    }

    lists.forEach((list, index) => {
      const imported = document.importNode(list, true);
      markCurrentLinks(imported);
      slots[index].replaceWith(imported);
    });
    nav.dataset.megaMenuState = 'ready';
  }

  /* Leaving the page mid-prefetch cancels the request, and that reads exactly like a network
     failure. Retrying once separates the two without having to tell them apart: a visitor who
     navigated away takes the pending timer with them, so the cancelled case degrades nothing
     and logs nothing, while a genuinely broken fetch fails twice and is reported once. It also
     rides out a transient blip, which is the common real cause. */
  function load(attemptsLeft) {
    fetch(source)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`mega-menu-panels: ${source} responded ${response.status}`);
        }
        return response.json();
      })
      .then((sections) => {
        const html = sections[sectionKey];
        if (typeof html !== 'string') {
          throw new Error(`mega-menu-panels: no "${sectionKey}" section in the response`);
        }
        inject(html);
      })
      .catch((error) => {
        if (attemptsLeft > 0) {
          window.setTimeout(() => load(attemptsLeft - 1), RETRY_DELAY);
          return;
        }
        degrade();
        console.error(error);
      });
  }

  /* Same breakpoint as base.css's `.header__inline-menu { display: block }`. */
  const desktopViewport = window.matchMedia('(min-width: 990px)');
  if (desktopViewport.matches) {
    load(1);
  } else {
    const onCross = (event) => {
      if (!event.matches) return;
      desktopViewport.removeEventListener('change', onCross);
      load(1);
    };
    desktopViewport.addEventListener('change', onCross);
  }
})();
